// eslint-disable-next-line import/order, import/extensions
import { cn } from '@/lib/utils';
import { useState, useEffect } from 'react';
import { Sparkles, ChevronUp, ChevronDown, PanelLeftClose, PanelLeft } from 'lucide-react';

import { usePathname } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import { useSubscription } from 'src/features/subscription/providers/subscription-provider';

import { useI18n } from 'src/i18n';
import { Logo } from 'src/components/common/logo';
import { Scrollbar } from 'src/components/common/scrollbar';
import { SparklesText } from 'src/components/ui/sparkles-text';

export type NavItem = {
  path: string;
  title: string;
  titleKey?: string;
  icon: React.ReactNode;
  info?: React.ReactNode;
  children?: NavItem[];
  external?: boolean;
};

export type NavContentProps = {
  data: NavItem[];
  slots?: {
    topArea?: React.ReactNode;
    bottomArea?: React.ReactNode;
  };
  className?: string;
  collapsed?: boolean;
  showHeader?: boolean;
  isDocked?: boolean;
  onToggleDock?: () => void;
};

// ----------------------------------------------------------------------

function AIBadge() {
  return (
    <>
      <style>{`
        @keyframes ai-aurora {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
      <span className="ml-auto mr-1 flex items-center justify-center gap-[3px] rounded-lg bg-zinc-100/80 px-2 py-[2px] font-semibold dark:bg-zinc-800/80">
        <Sparkles className="h-2.5 w-2.5 animate-pulse text-purple-600/70 dark:text-purple-400/80" />
        <span className="animate-[ai-aurora_4s_ease_infinite] bg-[linear-gradient(to_right,#3b82f6,#9333ea,#ec4899)] bg-[length:200%_200%] bg-clip-text text-[0.65rem] text-transparent dark:bg-[linear-gradient(to_right,#60a5fa,#c084fc,#f472b6)]">
          AI
        </span>
      </span>
    </>
  );
}

// ----------------------------------------------------------------------

export function NavDesktop({
  className,
  data,
  slots,
}: NavContentProps & { layoutQuery?: string }) {
  return (
    <nav
      className={cn(
        'fixed left-0 top-0 z-[var(--layout-nav-zIndex)] hidden h-full w-[var(--layout-nav-vertical-width)] flex-col border-r border-border/40 bg-white px-2.5 pt-2.5 dark:bg-zinc-950 md:flex',
        className,
      )}
    >
      <NavContent data={data} slots={slots} className={className} />
    </nav>
  );
}

// ----------------------------------------------------------------------

export function NavMobile({
  className,
  data,
  open,
  slots,
  onClose,
}: NavContentProps & { open: boolean; onClose: () => void }) {
  const pathname = usePathname();

  useEffect(() => {
    if (open) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-[1200] bg-black/30 backdrop-blur-sm"
          onClick={onClose}
          role="presentation"
        />
      )}
      {/* Drawer panel */}
      <div
        className={cn(
          'fixed left-0 top-0 z-[1300] h-full w-[var(--layout-nav-mobile-width)] overflow-auto bg-white px-5 pt-5 shadow-2xl transition-transform duration-300 dark:bg-zinc-950',
          open ? 'translate-x-0' : '-translate-x-full',
          className,
        )}
      >
        <NavContent data={data} slots={slots} className={className} />
      </div>
    </>
  );
}

// ----------------------------------------------------------------------

export function NavContent({
  data,
  slots,
  className,
  collapsed = false,
  showHeader = true,
  isDocked,
  onToggleDock,
}: NavContentProps) {
  const pathname = usePathname();
  const [openSubmenu, setOpenSubmenu] = useState<Record<string, boolean>>({});
  const { isPro } = useSubscription();
  const { t } = useI18n();

  const handleToggleSubmenu = (title: string) => {
    setOpenSubmenu((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  // Initialize open state based on current path
  useEffect(() => {
    const initialOpenState: Record<string, boolean> = {};

    data.forEach((item) => {
      if (item.children) {
        const shouldOpen = item.children.some(
          (child) => pathname.startsWith(child.path) || pathname === child.path
        );
        initialOpenState[item.title] = shouldOpen;
      }
    });

    setOpenSubmenu(initialOpenState);
  }, [pathname, data]);

  return (
    <div className="flex h-full flex-col">
      {showHeader && (
        <div className="mb-8 mt-6 flex h-14 flex-shrink-0 items-center px-4">
          {collapsed ? (
            <div className="flex w-[60px] items-center justify-center">
              <SparklesText
                text="J"
                className="text-2xl font-normal leading-none text-foreground"
                sparklesCount={3}
              />
            </div>
          ) : (
            <>
              <Logo />
              <div className="ml-3 flex flex-1 items-center gap-1.5 pt-0.5">
                <SparklesText
                  text="Jobjourney"
                  className="text-xl font-normal tracking-tight text-foreground"
                  sparklesCount={5}
                />

                {isPro && (
                  <span className="inline-flex items-center rounded bg-black px-1.5 py-0.5 text-[0.6rem] font-bold uppercase tracking-wider text-white dark:bg-white dark:text-black">
                    PRO
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {slots?.topArea}

      <Scrollbar
        fillContent
        className="flex-1 [&_.simplebar-track.simplebar-horizontal]:hidden [&_.simplebar-content-wrapper]:overflow-x-hidden"
      >
        <nav className={cn('flex flex-1 flex-col px-1', className)}>
          <ul className="flex flex-col gap-0.5">
            {data.map((item) => {
              const itemTitle = item.titleKey ? t(item.titleKey) : item.title;
              const isActive = item.path === pathname;
              const hasChildren = item.children && item.children.length > 0;
              const isOpen = openSubmenu[item.title] || false;

              const isActiveMenu =
                isActive ||
                (hasChildren && item.children?.some((child) => child.path === pathname));

              const showAIBadge =
                item.title === 'Profile' ||
                item.title === 'Saved Jobs' ||
                item.title === '1 min Portfolio';

              const ItemTag = hasChildren ? 'div' : item.external ? 'a' : RouterLink;
              const itemProps = hasChildren
                ? { onClick: () => handleToggleSubmenu(item.title), role: 'button' as const }
                : item.external
                  ? { href: item.path, target: '_blank' as const, rel: 'noopener noreferrer' }
                  : { href: item.path };

              return (
                <li key={item.path || item.title}>
                  <ItemTag
                    {...(itemProps as any)}
                    className={cn(
                      'grid min-h-[48px] cursor-pointer grid-cols-[48px_1fr] items-center rounded-xl py-2 pr-3 text-[0.9rem] font-medium text-muted-foreground no-underline transition-all duration-200 hover:bg-muted/60',
                      isActiveMenu && 'bg-muted font-semibold text-foreground shadow-sm',
                    )}
                  >
                    <span className="flex h-6 w-[48px] items-center justify-center">
                      {item.icon}
                    </span>

                    {!collapsed && item.title && (
                      <span className="flex flex-1 items-center justify-between truncate">
                        {itemTitle}
                        {showAIBadge && <AIBadge />}
                      </span>
                    )}

                    {item.info && item.info}

                    {hasChildren && (isOpen ? <ChevronUp className="h-4 w-4 opacity-50" /> : <ChevronDown className="h-4 w-4 opacity-50" />)}
                  </ItemTag>

                  {hasChildren && isOpen && (
                    <div className="ml-2">
                      {item.children?.map((child) => {
                        const childTitle = child.titleKey ? t(child.titleKey) : child.title;
                        const isChildActive = child.path === pathname;
                        const showChildAIBadge =
                          child.title === 'Profile' ||
                          child.title === 'Saved Jobs' ||
                          child.title === '1 min Portfolio';

                        return (
                          <RouterLink
                            key={child.title}
                            href={child.path}
                            className={cn(
                              'grid min-h-[42px] grid-cols-[48px_1fr] items-center rounded-xl py-1.5 text-[0.85rem] text-muted-foreground no-underline transition-all duration-200 hover:bg-muted/60',
                              isChildActive && 'bg-muted font-semibold text-foreground shadow-sm',
                            )}
                          >
                            <span className="flex h-6 w-[48px] items-center justify-center">
                              {child.icon}
                            </span>

                            {!collapsed && (
                              <span className="flex flex-1 items-center justify-between truncate">
                                {childTitle}
                                {showChildAIBadge && <AIBadge />}
                              </span>
                            )}

                            {child.info && child.info}
                          </RouterLink>
                        );
                      })}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
      </Scrollbar>

      {slots?.bottomArea}

      {/* Credit Section */}
      {!collapsed && (
        <div className="mt-auto relative flex flex-shrink-0 items-center justify-center gap-1.5 border-t border-border/40 px-4 py-4">
          <span className="text-xs text-muted-foreground">{t('menu.madeBy')}</span>
          <a
            href="https://www.linkedin.com/in/robert-wang-cs/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
            </svg>
          </a>
          
          {onToggleDock && (
            <button
              onClick={onToggleDock}
              className="absolute right-4 rounded p-1.5 text-muted-foreground hover:bg-muted/80 hover:text-foreground transition-all duration-200"
              aria-label={isDocked ? t('menu.undockSidebar') : t('menu.dockSidebar')}
            >
              {isDocked ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeft className="h-4 w-4" />}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
